import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import {
  FiTrendingUp, FiCheck, FiList, FiGlobe, FiLayers,
  FiFileText, FiCreditCard, FiBarChart2, FiExternalLink,
} from 'react-icons/fi';
import Reel from '../components/Reel';
import { getProduct } from '../data/catalog';

const SITE_LINK = 'https://myrankflow.com';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="group border border-border bg-card p-6 transition-colors hover:border-signal/50">
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-signal/10 transition-transform group-hover:scale-110">
      <SafeIcon icon={icon} className="h-6 w-6 text-signal" />
    </div>
    <h3 className="mb-2 font-display text-xl font-bold text-white">{title}</h3>
    <p className="text-sm leading-relaxed text-muted">{desc}</p>
  </div>
);

const SpecItem = ({ title, value }) => (
  <div className="flex items-center justify-between gap-4 border-b border-border py-3">
    <span className="shrink-0 text-sm text-muted">{title}</span>
    <span className="text-right font-mono text-sm text-primary">{value}</span>
  </div>
);

const MyRankFlow = () => {
  const PRODUCT = getProduct('myrankflow');

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="border-b border-border pb-16 pt-32">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-signal/20 bg-signal/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.16em] text-signal">
              <SafeIcon icon={FiTrendingUp} /> White-label SaaS
            </div>
            <h1 className="mb-6 font-display text-5xl font-extrabold tracking-tightest text-white md:text-7xl">
              Your backlink agency.<br />
              <span className="text-signal">Your brand on it.</span>
            </h1>
            <p className="mx-auto mb-10 max-w-3xl text-xl leading-relaxed text-muted">
              MyRankFlow is a rebrandable SEO storefront. Your clients order backlinks from a shop
              that carries your name, the platform delivers them, and you keep the margin. We stay
              invisible.
            </p>
            <div className="flex flex-col items-center justify-center gap-5 sm:flex-row">
              <a
                href={SITE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary bg-signal px-10 py-4 font-semibold text-background hover:bg-signal/90"
              >
                <SafeIcon icon={FiGlobe} className="h-5 w-5" />
                Open myrankflow.com
              </a>
              <div className="flex flex-col items-start text-left">
                <span className="font-mono font-bold text-white">{PRODUCT.price}</span>
                <span className="text-xs text-muted">One-time · lifetime access · no monthly platform fee</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Reel */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel — MyRankFlow</p>
          <Reel frames={PRODUCT.frames} label="MyRankFlow" sound={false} ratio="aspect-[16/10]" />
        </div>
      </section>

      {/* What you get */}
      <section className="border-y border-border bg-card/30 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold text-white">A shop, not a spreadsheet</h2>
            <p className="text-muted">Everything a reseller needs, and nothing they have to build.</p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <FeatureCard
              icon={FiLayers}
              title="25+ link services"
              desc="DA 30 to 70+, Web 2.0 blogs and EDU links, already priced and described. You choose your own margin on top."
            />
            <FeatureCard
              icon={FiCreditCard}
              title="Payments built in"
              desc="Clients pay you through the storefront. No separate checkout to wire up and no invoicing by hand."
            />
            <FeatureCard
              icon={FiFileText}
              title="Reports that go out as yours"
              desc="Each completed order produces a CSV your client can open, carrying your branding rather than ours."
            />
            <FeatureCard
              icon={FiBarChart2}
              title="Live dashboard"
              desc="Orders, delivery status and revenue in one place, so you know what is running without chasing it."
            />
          </div>
        </div>
      </section>

      {/* Multi-tenant explanation + specs */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-20 lg:flex-row">
            <div className="flex-1 space-y-8">
              <h2 className="font-display text-4xl font-bold leading-tight text-white">
                White label means<br />
                <span className="text-signal">your clients never see us.</span>
              </h2>
              <p className="text-muted">
                Every account gets its own tenant: its own storefront, its own domain-facing branding,
                its own client list and its own order history. Nothing is shared between resellers, and
                nothing on the customer-facing side carries the MyRankFlow name.
              </p>
              <div className="space-y-6">
                {[
                  ['Your storefront', 'Your name, your logo, your prices. Set your margin per service.'],
                  ['Your clients', 'They order from you and hear from you. Fulfilment happens behind the curtain.'],
                  ['No monthly platform fee', 'A one-time licence rather than a subscription that eats the margin you just made.'],
                ].map(([t, d]) => (
                  <div key={t} className="flex gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-signal/10 text-signal">
                      <SafeIcon icon={FiCheck} />
                    </div>
                    <p className="text-muted">
                      <strong className="text-white">{t}:</strong> {d}
                    </p>
                  </div>
                ))}
              </div>
              <a
                href={SITE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-medium text-white transition-colors hover:text-signal"
              >
                See the live pricing on myrankflow.com <SafeIcon icon={FiExternalLink} />
              </a>
            </div>

            <div className="w-full max-w-md flex-1">
              <div className="border border-border bg-card p-8">
                <h3 className="mb-6 flex items-center gap-2 font-display text-xl font-bold text-white">
                  <SafeIcon icon={FiList} className="text-signal" /> At a glance
                </h3>
                <div className="space-y-1">
                  <SpecItem title="Type" value="Hosted, multi-tenant" />
                  <SpecItem title="Platform" value="Web — nothing to install" />
                  <SpecItem title="Services" value="25+" />
                  <SpecItem title="Branding" value="Fully white label" />
                  <SpecItem title="Billing" value="One-time licence" />
                  <SpecItem title="Languages" value="English and Spanish" />
                </div>
                <div className="mt-8 border-t border-border pt-6">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="font-bold text-white">Launch price</span>
                    <span className="font-mono text-xl font-bold text-signal">{PRODUCT.price}</span>
                  </div>
                  <p className="mb-6 text-xs text-muted">
                    One-time payment for lifetime access, and $10 of credit towards your first order.
                    Current pricing always lives on myrankflow.com.
                  </p>
                  <a
                    href={SITE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full items-center justify-center gap-2 bg-white py-3 font-bold text-background transition-colors hover:bg-gray-200"
                  >
                    Start your agency
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-24 text-center">
        <div className="absolute inset-0 -z-10 bg-signal/5" />
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="mb-6 font-display text-4xl font-bold text-white">
            Sell SEO without doing SEO.
          </h2>
          <p className="mb-10 text-muted">
            The fulfilment is handled. You bring the clients, set the prices, and the storefront does
            the rest under your name.
          </p>
          <a
            href={SITE_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary mx-auto w-fit bg-signal px-12 py-5 font-semibold text-background hover:bg-signal/90"
          >
            <SafeIcon icon={FiGlobe} className="h-5 w-5" />
            Open myrankflow.com
          </a>
        </div>
      </section>
    </div>
  );
};

export default MyRankFlow;
